import { memo, useEffect, useState } from "react"
import css from "../RouteView.module.css"

import classNames from "classnames"
import { createListenerMiddleware } from "@reduxjs/toolkit"
import { isMobile } from "@/utils/detectDevice"
import AdHocCommands from "../../api/adHocCommands"
import MapErrorBoundary from "./MapErrorBoundary"
import { createPortal } from "react-dom"
import MapView from "./MapView"
import FullScreenMap from "@/components/maps/FullScreenMap"
import { useMediaQuery } from "@react-hookz/web"
import { APIProvider } from "@vis.gl/react-google-maps"
import { RouteStop } from "@/reducers/routes2gSlice"
import type { TrafficIncident, TrafficRange } from "./routeTraffic"
import { useAppSelector } from "@/reducers/hooks"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"

export type Leg = {
  polyline: string
  distance: number
  duration: number
  base_duration?: number
  stop?: RouteStop
  /** HERE traffic, only with the `route-traffic` flag */
  traffic?: TrafficRange[]
  incidents?: TrafficIncident[]
}

export type Waypoint = {
  location: [number, number]
}

export type Route = {
  legs: Leg[]
  distance: number
  tolls?: Toll[]
  stops?: RouteStop[]
  id?: string
  label?: string
  fuel?: number
  isCustom?: boolean
  isAlternative?: boolean
}

export type TrafficSection = {
  startPointIndex: number
  endPointIndex: number
  sectionType: string
  simpleCategory: string
  effectiveSpeedInKmh: number
  delayInSeconds: number
  magnitudeOfDelay: number
  tec: {
    causes: Array<{
      mainCauseCode: number
    }>
    effectCode: number
  }
}

export type Toll = {
  fare_id?: string
  country_code: string
  location?: {
    lat: number
    lng: number
  }
  price: number
  currency: string
}

export type RouteInfo = {
  routes: Route[]
  waypoints?: Waypoint[]
  sections?: TrafficSection[]
  tolls?: Toll[]
  stops?: RouteStop[]
  id?: string
  /** When gps got the HERE traffic in the legs (ISO, UTC) */
  traffic_fetched_at?: string
}

export const mapUpdateListener = createListenerMiddleware()

type RouteViewProps = {
  driverJid: string
  locationJid?: string
  containerSize: { width: number; height: number }
  isReadOnlyMap: boolean
  /** Static preview — map + route only, no controls. */
  isSnapshot?: boolean
  hideRoutePanel?: boolean
}

const RouteView = ({
  driverJid,
  locationJid,
  isReadOnlyMap = false,
  isSnapshot = false,
  hideRoutePanel = false,
}: RouteViewProps) => {
  const isSmallScreen = useMediaQuery("(max-width: 900px)")

  const [isFullScreen, setIsFullScreen] = useState(false)
  const { activeTab } = useAppSelector(selectTabs)

  useEffect(() => {
    if (activeTab !== TAB_TYPES.CHATS) {
      setIsFullScreen(false)
    }
  }, [activeTab])

  return (
    <APIProvider apiKey={import.meta.env.REACT_APP_GOOGLE_MAP_KEY}>
      {isMobile || isSmallScreen ? (
        // Mobile view without resizable component
        <div
          id="mobileMap"
          className={classNames(css.map, "dark:bg-gray-900")}
          style={{
            width: window.innerWidth + "px",
            height: window.innerHeight + "px",
          }}
        >
          <MapView
            isReadOnlyMap={isReadOnlyMap}
            driverJid={driverJid}
            locationJid={locationJid}
            onOpenFullScreen={() => setIsFullScreen(true)}
            hideRoutePanel={hideRoutePanel}
          />
        </div>
      ) : isFullScreen ? (
        createPortal(
          <FullScreenMap
            driverJid={driverJid}
            locationJid={locationJid}
            isReadOnlyMap={isReadOnlyMap}
            onCloseFullScreen={() => {
              if (window.isRouteUpdateTrackingCountdownActive) {
                AdHocCommands.upsertReferenceRoute(window.XMPP!, driverJid)
                window.isRouteUpdateTrackingCountdownActive = false
              }
              setIsFullScreen(false)
            }}
            onOpenFullScreen={() => setIsFullScreen(true)}
          />,
          document.body,
        )
      ) : (
        <div
          id="mapWithRoute"
          className={classNames(
            css.map,
            "bg-[var(--panel-color)]",
            "h-full w-full",
          )}
        >
          <MapErrorBoundary>
            <MapView
              driverJid={driverJid}
              locationJid={locationJid}
              onOpenFullScreen={() => setIsFullScreen(true)}
              isReadOnlyMap={isReadOnlyMap}
              isSnapshot={isSnapshot}
              hideRoutePanel={hideRoutePanel}
            />
          </MapErrorBoundary>
        </div>
      )}
    </APIProvider>
  )
}

export default memo(RouteView)
